import React, { useContext,useState, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const TraderDashboard = () => {
  const [tradeData, setTradeData] = useState([]);
  const [viewMode, setViewMode] = useState("pending");
  const [custodianFilter, setCustodianFilter] = useState("");
const [accountFilter, setAccountFilter] = useState("");
const [buySellFilter, setBuySellFilter] = useState("Both");
const [dateFrom, setDateFrom] = useState("");
const [dateTo, setDateTo] = useState("");
const [firmFilter, setFirmFilter] = useState("all");      // 'all' or a specific firm_slug
const [selectedMonths, setSelectedMonths] = useState(1);  // 1 or 2 months for processed tab

  const [expandedCustodians, setExpandedCustodians] = useState({});
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [expandedFirms, setExpandedFirms] = useState({});   // firm boxes: default open (undefined/true = open, false = collapsed)
  const [firmNames, setFirmNames] = useState({});           // firm_slug -> display_name for the firm headers
  const [showCannotSubmitModal, setShowCannotSubmitModal] = useState(false);
  const [page, setPage] = useState(1);
const [totalPages, setTotalPages] = useState(1);
const [cannotSubmitReason, setCannotSubmitReason] = useState('');
const [selectedTrade, setSelectedTrade] = useState(null);

// ── Multi-select state ──────────────────────────────────────────────────────
const [selectedTradeIds, setSelectedTradeIds] = useState(new Set());
const [showMixedBulkModal, setShowMixedBulkModal] = useState(false);
const [mixedBulkAccount, setMixedBulkAccount] = useState(null);
const [mixedBulkTrades, setMixedBulkTrades] = useState([]);
const [mixedTradeActions, setMixedTradeActions] = useState({}); // { tradedetailid: 'complete' | 'cannot_submit' }
const [mixedBulkReason, setMixedBulkReason] = useState('');
// ───────────────────────────────────────────────────────────────────────────

// ── Sort state ──────────────────────────────────────────────────────────────
const [sortColumn, setSortColumn] = useState(null);
const [sortDirection, setSortDirection] = useState(null); // null → 'asc' → 'desc' → null
// ───────────────────────────────────────────────────────────────────────────

    const {
    registeredUser,
    authorizedFirms,
    setAuthorizedFirms,
    logout
  } = useContext(AuthContext);

  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

// Clear selections and sort whenever view mode changes
useEffect(() => {
  setSelectedTradeIds(new Set());
  setSortColumn(null);
  setSortDirection(null);
}, [viewMode]);

useEffect(() => {
  const fetchTrades = async () => {
    try {
      const params = new URLSearchParams({
        page,
        limit : 50,
        status: viewMode,
        firm  : firmFilter,
        months: selectedMonths,
      });
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/trader-trades?${params}`,
        {
          credentials: 'include',
          headers    : { 'Content-Type': 'application/json' }
        }
      );
      if (response.status === 403 || response.status === 401) {
        navigate('/login');
        return;
      }
      const result = await response.json();
      setTradeData(result.data);
      setTotalPages(result.totalPages);
      // Sync authorizedFirms from API response into context (keeps firm dropdown up to date)
      if (result.authorizedFirms?.length) {
        setAuthorizedFirms(result.authorizedFirms);
      }
    } catch (err) {
      console.error('Error fetching trades:', err);
    }
  };

  if (!registeredUser) {
    navigate('/login');
  } else {
    fetchTrades();
  }
}, [registeredUser, navigate, page, viewMode, firmFilter, selectedMonths, setAuthorizedFirms]);
const custodianOptions = [...new Set(tradeData.map(t => t.custodian_name))];
const filteredTrades = tradeData.filter(trade => {

  const status = trade.status?.trim().toLowerCase();

  // Status filter (existing logic)
  if (viewMode === "pending" && status !== "pending") return false;

  if (
    viewMode === "processed" &&
    status !== "completed" &&
    status !== "cannot submit"
  ) return false;

  // Custodian filter
  if (custodianFilter && trade.custodian_name !== custodianFilter)
    return false;

  // Account filter
  if (
    accountFilter &&
    !trade.accountnumber.toLowerCase().includes(accountFilter.toLowerCase())
  )
    return false;

  // Buy/Sell filter
 if (buySellFilter === "Buy" && trade.buysell !== "B")
  return false;

if (buySellFilter === "Sell" && trade.buysell !== "S")
  return false;
  // Date range filter
const tradeDate =
  viewMode === "processed"
    ? new Date(trade.updateddatetime)
    : new Date(trade.requesteddate);

if (dateFrom) {
  const from = new Date(dateFrom + "T00:00:00Z"); // 👈 only this line changed
  if (tradeDate < from) return false;
}

if (dateTo) {
  const to = new Date(dateTo + "T00:00:00Z");     // 👈 this line changed
  to.setUTCDate(to.getUTCDate() + 1);

  if (tradeDate >= to) return false;
}

  return true;
});
  const sortedTrades = (() => {
  if (!sortColumn || !sortDirection) return filteredTrades;
  return [...filteredTrades].sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];
    // Date columns
    if (sortColumn === 'requesteddate' || sortColumn === 'updateddatetime') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
      return sortDirection === 'asc' ? bVal - aVal : aVal - bVal;
    }
    // Number columns
    if (sortColumn === 'quantity' || sortColumn === 'percentage') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
      return sortDirection === 'asc' ? bVal - aVal : aVal - bVal;
    }
    // String columns
    aVal = (aVal || '').toString().toLowerCase();
    bVal = (bVal || '').toString().toLowerCase();
    return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });
})();

  // Group by FIRM -> CUSTODIAN -> ACCOUNT -> trades. A firm only shows the
  // custodians/accounts it actually has trades for (falls out of the grouping).
  const groupedData = sortedTrades.reduce((acc, trade) => {
    const { firm_slug, custodian_name, accountnumber } = trade;
    const firm = firm_slug || 'unknown';

    if (!acc[firm]) acc[firm] = {};
    if (!acc[firm][custodian_name]) acc[firm][custodian_name] = {};
    if (!acc[firm][custodian_name][accountnumber]) acc[firm][custodian_name][accountnumber] = [];

    acc[firm][custodian_name][accountnumber].push(trade);
    return acc;
  }, {});
useEffect(() => {
  if (viewMode === "processed") {
    const expanded = {};
    Object.values(groupedData).forEach(custodians => {
      Object.keys(custodians).forEach(custodian => { expanded[custodian] = true; });
    });
    setExpandedCustodians(expanded);
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [viewMode, tradeData]);
  const toggleCustodian = (name) => {
    setExpandedCustodians(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const toggleAccount = (accNum) => {
    setExpandedAccounts(prev => ({ ...prev, [accNum]: !prev[accNum] }));
  };

  // Firm boxes default to expanded; false = collapsed.
  const toggleFirm = (slug) => {
    setExpandedFirms(prev => ({ ...prev, [slug]: prev[slug] === false }));
  };
  const firmDisplayName = (slug) => firmNames[slug] || slug;

  // Load firm_slug -> display_name once, for the firm group headers.
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/trader-firms`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : []))
      .then(rows => {
        const map = {};
        (Array.isArray(rows) ? rows : []).forEach(f => { if (f.firm_slug) map[f.firm_slug] = f.display_name || f.firm_slug; });
        setFirmNames(map);
      })
      .catch(() => {});
  }, []);
const toLocalTime = (dateStr) => {
  if (!dateStr) return "";

  return new Date(dateStr).toLocaleString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short"
  });
};

// ── Sort helpers ─────────────────────────────────────────────────────────────
const handleSort = (column) => {
  if (sortColumn !== column) {
    setSortColumn(column);
    setSortDirection('asc');
  } else if (sortDirection === 'asc') {
    setSortDirection('desc');
  } else {
    setSortColumn(null);
    setSortDirection(null);
  }
};

const getSortIndicator = (column) => {
  if (sortColumn !== column) return ' ▼';
  if (sortDirection === 'asc') return ' ▲';
  return ' ▼';
};
// ───────────────────────────────────────────────────────────────────────────

// ── Checkbox helpers ────────────────────────────────────────────────────────
const toggleTradeSelection = (tradedetailid) => {
  setSelectedTradeIds(prev => {
    const next = new Set(prev);
    if (next.has(tradedetailid)) {
      next.delete(tradedetailid);
    } else {
      next.add(tradedetailid);
    }
    return next;
  });
};

const toggleAccountSelection = (trades) => {
  const allSelected = trades.every(t => selectedTradeIds.has(t.tradedetailid));
  setSelectedTradeIds(prev => {
    const next = new Set(prev);
    if (allSelected) {
      trades.forEach(t => next.delete(t.tradedetailid));
    } else {
      trades.forEach(t => next.add(t.tradedetailid));
    }
    return next;
  });
};
// ───────────────────────────────────────────────────────────────────────────

const openCannotSubmitModal = (trade) => {
  setSelectedTrade(trade);
  setCannotSubmitReason('');
  setShowCannotSubmitModal(true);
};

// ── Open mixed bulk modal ───────────────────────────────────────────────────
const openMixedBulkModal = (accountNumber, trades) => {
  const accountSelectedTrades = trades.filter(t => selectedTradeIds.has(t.tradedetailid));
  const initialActions = {};
  accountSelectedTrades.forEach(t => { initialActions[t.tradedetailid] = 'cannot_submit'; });
  setMixedBulkAccount(accountNumber);
  setMixedBulkTrades(accountSelectedTrades);
  setMixedTradeActions(initialActions);
  setMixedBulkReason('');
  setShowMixedBulkModal(true);
};

const toggleMixedTradeAction = (tradedetailid) => {
  setMixedTradeActions(prev => ({
    ...prev,
    [tradedetailid]: prev[tradedetailid] === 'complete' ? 'cannot_submit' : 'complete'
  }));
};
// ───────────────────────────────────────────────────────────────────────────

const markTradeAsComplete = async (tradedetailid, firm_slug) => {
   const confirmAction = window.confirm("Are you sure you want to mark this trade as complete?");
  if (!confirmAction)
    return;

  try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/mark-complete`, {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        // updatedby removed — server derives actor identity from JWT (ISO 27001 A.5.28 / SOC 2 CC7.2)
        // firm_slug tells the backend which schema to update (ISO 27001 A.9 / SOC 2 CC6.3)
        body: JSON.stringify({ tradedetailid, firm_slug }),
      });

      await response.json();

    // Step 2: Update local tradeData
    const updatedTradeData = tradeData.map(trade =>
      trade.tradedetailid === tradedetailid
        ? { ...trade, status: 'Completed' }
        : trade
    );
    setTradeData(updatedTradeData);


   // Step 3: Get tradeid of updated item
    const updatedTrade = updatedTradeData.find(t => t.tradedetailid === tradedetailid);
    const currentTradeAcc = updatedTrade.accountnumber;
    //const currentAdvisoremail=updatedTrade.username



    // Step 4: Notify user immediately — irrespective of other pending trades
    await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notify-user`, {
      method: "POST",
      credentials: 'include',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        accountNumber: currentTradeAcc,
        tradedetailid: updatedTrade.tradedetailid,  // scopes email to only the advisor who submitted this trade
        firm_slug    : updatedTrade.firm_slug,
        tradeDetails: [{ symbol: updatedTrade.symbol, buysell: updatedTrade.buysell }]
      }),
    });

    setViewMode("processed");
    setPage(1);

    const updatedData = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/trader-trades?page=1&limit=50&status=processed&firm=${firmFilter}&months=${selectedMonths}`,
      { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
    );
    if (updatedData.status === 401 || updatedData.status === 403) {
      console.error('Unauthorized. Redirecting...');
      return;
    }
    const updatedJson = await updatedData.json();
    setTradeData(updatedJson.data);
    setTotalPages(updatedJson.totalPages);
    setViewMode("processed");
  } catch (error) {
    console.error("Error in markTradeAsComplete:", error);
  }
};

  const handleLoginToCustodian = async (accountNumber, custodian, firmSlug) => {

    const confirmLogin = window.confirm(
      `Are you sure you want to login to the custodian "${custodian}" for account number :  "${accountNumber}"?`
    );

    if (!confirmLogin) {
      return; // User cancelled
    }

    try {
      const response1 = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/login-to-custodian`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        // requestedby removed — server derives actor identity from JWT (ISO 27001 A.5.28 / SOC 2 CC7.2)
        // firm_slug tells the backend which schema to query for credentials (ISO 27001 A.9 / SOC 2 CC6.3)
        body: JSON.stringify({ accountNumber, custodian, firm_slug: firmSlug }),
      });

      const result = await response1.json();

      if (result.success) {
        // Custodian credentials must never be logged — removed console.log (ISO 27001 A.8.10 / SOC 2 CC6.1)

        const response2 = await fetch(`${process.env.REACT_APP_LOCAL_APP_API_BASE_URL}/loginautomation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountNumber,
            custodian,
            logindata: result.payload,
            loginurl : result.loginurl,
            // Multi-firm: forward the schema/custodian identifiers so the local agent
            // creates a login_sessions row identical to the scraper (same Twilio webhook fills OTP)
            firm_slug: result.firm_slug ?? firmSlug,
            custodianId: result.custodian_id,
            custodianAccountId: result.custodian_account_id,
            clientId: result.client_id,
          }),
        });

        const autoResult = await response2.json();
        if (autoResult.success){
        // ISO 27001 A.8.12: payload must never be logged — may contain custodian credentials
         alert("login sucessfull")
        }

      } else {
        alert('Login failed: ' + result.message);
      }

    } catch (error) {
      console.error('Error logging in:', error);
      alert('An unexpected error occurred.');
    }
  };

// ── Single trade cannot submit (existing flow) ──────────────────────────────
const handleCannotSubmit = async () => {
  if (!cannotSubmitReason.trim()) {
    alert("Please enter a reason.");
    return;
  }

  try {
    // 1️⃣ Update status
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/trade-cannot-submit`,
      {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        // updatedby removed — server derives actor identity from JWT (ISO 27001 A.5.28)
        // firm_slug tells the backend which schema to update (ISO 27001 A.9 / SOC 2 CC6.3)
        body: JSON.stringify({
          tradedetailid: selectedTrade.tradedetailid,
          firm_slug    : selectedTrade.firm_slug,
        })
      }
    );

    const result = await response.json();

    if (!result.success) {
      alert("Failed to update trade.");
      return;
    }

    // 2️⃣ Notify user
    await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/notify-cannot-submit`,
      {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountNumber: result.accountNumber,
          tradedetailid: selectedTrade.tradedetailid,  // scopes email to only the advisor who submitted this trade
          firm_slug    : selectedTrade.firm_slug,
          reason       : cannotSubmitReason
        })
      }
    );

    // 3️⃣ Refresh UI
    setViewMode("processed");
    setPage(1);

    const updatedData = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/trader-trades?page=1&limit=50&status=processed&firm=${firmFilter}&months=${selectedMonths}`,
      { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
    );
    const updatedJson = await updatedData.json();
    setTradeData(updatedJson.data);
    setTotalPages(updatedJson.totalPages);

    setShowCannotSubmitModal(false);
    setCannotSubmitReason("");

  } catch (error) {
    console.error(error);
    alert("Unexpected error occurred.");
  }
};
// ───────────────────────────────────────────────────────────────────────────

// ── Bulk mark complete handler ──────────────────────────────────────────────
const handleBulkMarkComplete = async (accountNumber, trades) => {
  const accountSelectedTrades = trades.filter(t => selectedTradeIds.has(t.tradedetailid));

  const confirmAction = window.confirm(
    `Are you sure you want to mark ${accountSelectedTrades.length} trade(s) as complete for account ${accountNumber}?`
  );
  if (!confirmAction) return;

  try {
    // 1️⃣ Mark all selected trades complete in one call
    // All trades in one account bulk action share the same firm_slug
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/bulk-mark-complete`,
      {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        // updatedby removed — server derives actor identity from JWT (ISO 27001 A.5.28)
        body: JSON.stringify({
          tradedetailids: accountSelectedTrades.map(t => t.tradedetailid),
          firm_slug     : accountSelectedTrades[0]?.firm_slug,
        })
      }
    );

    const result = await response.json();

    if (!result.success) {
      alert("Failed to mark trades as complete.");
      return;
    }

    // 2️⃣ Update local state
    const updatedTradeData = tradeData.map(trade =>
      accountSelectedTrades.some(st => st.tradedetailid === trade.tradedetailid)
        ? { ...trade, status: 'Completed' }
        : trade
    );

    // 3️⃣ If ALL trades in this account are now complete, send one notification
    const sameAccountTrades = updatedTradeData.filter(t => t.accountnumber === result.accountNumber);
    const allCompleted = sameAccountTrades.every(t => t.status === 'Completed');

    if (allCompleted) {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notify-user`, {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountNumber: result.accountNumber,
          tradedetailid: accountSelectedTrades[0]?.tradedetailid,  // scopes email to only the advisor who submitted
          firm_slug    : accountSelectedTrades[0]?.firm_slug,
        })
      });
    }

    // 4️⃣ Clear selections & refresh
    setSelectedTradeIds(new Set());
    setViewMode("processed");
    setPage(1);

    const updatedData = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/trader-trades?page=1&limit=50&status=processed&firm=${firmFilter}&months=${selectedMonths}`,
      { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
    );

    const updatedJson = await updatedData.json();
    setTradeData(updatedJson.data);
    setTotalPages(updatedJson.totalPages);

  } catch (error) {
    console.error(error);
    alert("Unexpected error occurred.");
  }
};
// ───────────────────────────────────────────────────────────────────────────

// ── Mixed bulk action handler ───────────────────────────────────────────────
const handleMixedBulkAction = async () => {
  const completeTrades = mixedBulkTrades.filter(t => mixedTradeActions[t.tradedetailid] === 'complete');
  const cannotSubmitTrades = mixedBulkTrades.filter(t => mixedTradeActions[t.tradedetailid] === 'cannot_submit');

  if (cannotSubmitTrades.length > 0 && !mixedBulkReason.trim()) {
    alert("Please enter a reason for trades that cannot be submitted.");
    return;
  }

  try {
    let accountNumber = mixedBulkAccount;

    // 1️⃣ Mark complete trades (if any)
    if (completeTrades.length > 0) {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bulk-mark-complete`, {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        // updatedby removed — server derives actor identity from JWT (ISO 27001 A.5.28)
        body: JSON.stringify({
          tradedetailids: completeTrades.map(t => t.tradedetailid),
          firm_slug     : completeTrades[0]?.firm_slug,
        })
      });
      const r = await res.json();
      if (!r.success) { alert("Failed to mark trades as complete."); return; }
      accountNumber = r.accountNumber || accountNumber;
    }

    // 2️⃣ Mark cannot-submit trades (if any)
    if (cannotSubmitTrades.length > 0) {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bulk-cannot-submit`, {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        // updatedby removed — server derives actor identity from JWT (ISO 27001 A.5.28)
        body: JSON.stringify({
          tradedetailids: cannotSubmitTrades.map(t => t.tradedetailid),
          firm_slug     : cannotSubmitTrades[0]?.firm_slug,
        })
      });
      const r = await res.json();
      if (!r.success) { alert("Failed to update cannot-submit trades."); return; }
    }

    // 3️⃣ Send appropriate notification email
    if (completeTrades.length > 0 && cannotSubmitTrades.length > 0) {
      // Mixed — one combined summary email
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notify-mixed-bulk`, {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountNumber,
          tradedetailid      : completeTrades[0]?.tradedetailid || cannotSubmitTrades[0]?.tradedetailid,  // scopes to submitting advisor
          firm_slug          : completeTrades[0]?.firm_slug || cannotSubmitTrades[0]?.firm_slug,
          completedTrades    : completeTrades.map(t => ({ symbol: t.symbol, buysell: t.buysell })),
          cannotSubmitTrades : cannotSubmitTrades.map(t => ({ symbol: t.symbol, buysell: t.buysell })),
          reason             : mixedBulkReason
        })
      });
    } else if (completeTrades.length > 0) {
      // All complete — use existing notify-user
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notify-user`, {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountNumber,
          tradedetailid: completeTrades[0]?.tradedetailid,  // scopes to submitting advisor
          firm_slug   : completeTrades[0]?.firm_slug,
          tradeDetails: completeTrades.map(t => ({ symbol: t.symbol, buysell: t.buysell }))
        })
      });
    } else {
      // All cannot-submit — use existing notify-cannot-submit
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notify-cannot-submit`, {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountNumber,
          tradedetailid: cannotSubmitTrades[0]?.tradedetailid,  // scopes to submitting advisor
          firm_slug   : cannotSubmitTrades[0]?.firm_slug,
          reason      : mixedBulkReason,
          tradeSymbols: cannotSubmitTrades.map(t => `${t.buysell === 'B' ? 'Buy' : 'Sell'} ${t.symbol}`)
        })
      });
    }

    // 4️⃣ Clear selections & refresh UI
    setSelectedTradeIds(new Set());
    setShowMixedBulkModal(false);
    setMixedBulkReason('');
    setViewMode("processed");
    setPage(1);

    const updatedData = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/trader-trades?page=1&limit=50&status=processed&firm=${firmFilter}&months=${selectedMonths}`,
      { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
    );
    const updatedJson = await updatedData.json();
    setTradeData(updatedJson.data);
    setTotalPages(updatedJson.totalPages);

  } catch (error) {
    console.error(error);
    alert("Unexpected error occurred.");
  }
};
// ───────────────────────────────────────────────────────────────────────────


 return (
  <div className="dashboard-pageTrader">

    <div className="dashboard-headerTrader">
      <h2>Trade Dashboard</h2>
      {/* ✅ ADD TOGGLE HERE */}
  <div style={{ marginTop: "10px" }}>
    <button
  onClick={() => {
    setViewMode("pending");
    setPage(1);
  }}
      style={{
        marginRight: "10px",
        backgroundColor: viewMode === "pending" ? "#7a449a" : "#ccc",
        color: "white",
        padding: "6px 12px",
        border: "none",
        cursor: "pointer"
      }}
    >
      Pending Trades
    </button>

    <button
  onClick={() => {
    setViewMode("processed");
    setPage(1);
  }}
      style={{
        backgroundColor: viewMode === "processed" ? "#7a449a" : "#ccc",
        color: "white",
        padding: "6px 12px",
        border: "none",
        cursor: "pointer"
      }}
    >
      Processed Trades
    </button>
  </div>
      <div className="header-buttonsTrader">
        {registeredUser?.isAdmin && (
          <div>
            <button onClick={() => navigate('/invite-user')}>
              Invite User
            </button>
          </div>
        )}

        <div>
          <button onClick={handleLogout}>Logout</button>
        </div>
      </div>
    </div>
{viewMode === "processed" && (
<div className="filter-bar">

  <div className="filter-row">

    <div className="filter-field" style={{ gridColumn: 'span 2' }}>
      <label>Firm</label>
      <select
        value={firmFilter}
        onChange={(e) => { setFirmFilter(e.target.value); setPage(1); }}
      >
        <option value="all">All Firms</option>
        {authorizedFirms.map((f) => (
          <option key={f} value={f}>{f}</option>
        ))}
      </select>
    </div>

    <div className="filter-field" style={{ gridColumn: 'span 2' }}>
      <label>Date Range</label>
      <select
        value={selectedMonths}
        onChange={(e) => { setSelectedMonths(Number(e.target.value)); setPage(1); }}
      >
        <option value={1}>Last 1 Month</option>
        <option value={2}>Last 2 Months</option>
      </select>
    </div>

    <div className="filter-field" style={{ gridColumn: 'span 2' }}>
      <label>Custodian</label>
      <select
        value={custodianFilter}
        onChange={(e) => setCustodianFilter(e.target.value)}
      >
        <option value="">All Custodians</option>
        {custodianOptions.map((custodian) => (
          <option key={custodian} value={custodian}>
            {custodian}
          </option>
        ))}
      </select>
    </div>

    <div className="filter-field" style={{ gridColumn: 'span 2' }}>
      <label>Buy/Sell</label>
      <select
        value={buySellFilter}
        onChange={(e) => setBuySellFilter(e.target.value)}
      >
        <option value="Both">Both</option>
        <option value="Buy">Buy</option>
        <option value="Sell">Sell</option>
      </select>
    </div>

    <div className="filter-field">
      <label>Account #</label>
      <input
        type="text"
        placeholder="Search account"
        value={accountFilter}
        onChange={(e) => setAccountFilter(e.target.value)}
      />
    </div>

    <div className="filter-field">
      <label>Date From</label>
      <input
        type="date"
        value={dateFrom}
        onChange={(e) => setDateFrom(e.target.value)}
      />
    </div>

    <div className="filter-field">
      <label>Date To</label>
      <input
        type="date"
        value={dateTo}
        onChange={(e) => setDateTo(e.target.value)}
      />
    </div>

    <div className="filter-buttons">
      <button
        className="clear-btn"
        onClick={() => {
          setCustodianFilter("");
          setAccountFilter("");
          setBuySellFilter("Both");
          setDateFrom("");
          setDateTo("");
          setFirmFilter("all");
          setSelectedMonths(1);
          setPage(1);
        }}
      >
        Clear
      </button>
    </div>

  </div>

</div>
)}

    <div className="dashboardTrader">
      {Object.entries(groupedData).map(([firmSlug, custodians]) => (
        <div key={firmSlug} className="firm-header" style={{ marginBottom: '16px', border: '1px solid #d8cbe8', borderRadius: '8px', overflow: 'hidden' }}>
          <div
            onClick={() => toggleFirm(firmSlug)}
            style={{ cursor: 'pointer', fontWeight: 'bold', padding: '12px 16px', background: '#6A0DAD', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span>{firmDisplayName(firmSlug)}</span>
            <span>{expandedFirms[firmSlug] !== false ? '▼' : '▶️'}</span>
          </div>
          {expandedFirms[firmSlug] !== false && (
            <div style={{ padding: '8px' }}>
      {Object.entries(custodians).map(([custodian, accounts]) => (
        <div key={custodian} className="custodian-header">

          <div
            onClick={() => toggleCustodian(custodian)}
          >
            <span>{custodian}</span>
            <span>{expandedCustodians[custodian] ? '▼' : '▶️'}</span>
          </div>

          {expandedCustodians[custodian] && (
            <div className="p-3">

              {Object.entries(accounts).map(([accountNumber, trades]) => {
                // Count how many trades in this account are selected
                const accountSelectedCount = trades.filter(t => selectedTradeIds.has(t.tradedetailid)).length;
                const allAccountSelected = accountSelectedCount === trades.length && trades.length > 0;

                return (
                <div key={accountNumber} className="account-header">

                  <div
                    onClick={() => toggleAccount(accountNumber)}
                  >
                        <span
                          className={viewMode === "pending" ? "text-blue-600 underline cursor-pointer" : ""}
                          onClick={(e) => {
                            if (viewMode !== "pending") return;
                            e.stopPropagation();
                            handleLoginToCustodian(accountNumber, custodian, trades[0]?.firm_slug);
                          }}
                        >
                          Account: {accountNumber}
                        </span>

                    <span>{expandedAccounts[accountNumber] ? '▼' : '▶️'}</span>
                  </div>

                  {expandedAccounts[accountNumber] && (
                    <div style={{ overflowX: "auto", marginTop: "8px" }}>

                      <table className="trade-table">
                        <thead>
                          <tr>
                            {/* Checkbox header — only shown in pending view */}
                            {viewMode === "pending" && (
                              <th style={{ width: "40px", textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  title="Select all in this account"
                                  checked={allAccountSelected}
                                  onChange={() => toggleAccountSelection(trades)}
                                />
                              </th>
                            )}
                            <th onClick={() => viewMode === 'pending' && handleSort('buysell')} style={{ cursor: viewMode === 'pending' ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>Buy/Sell{viewMode === 'pending' ? ` ${getSortIndicator('buysell')}` : ''}</th>
                            <th onClick={() => viewMode === 'pending' && handleSort('sharedollar')} style={{ cursor: viewMode === 'pending' ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>Dollar/Share{viewMode === 'pending' ? ` ${getSortIndicator('sharedollar')}` : ''}</th>
                            <th onClick={() => viewMode === 'pending' && handleSort('quantity')} style={{ cursor: viewMode === 'pending' ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>Quantity{viewMode === 'pending' ? ` ${getSortIndicator('quantity')}` : ''}</th>
                            <th onClick={() => viewMode === 'pending' && handleSort('percentage')} style={{ cursor: viewMode === 'pending' ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>Percentage %{viewMode === 'pending' ? ` ${getSortIndicator('percentage')}` : ''}</th>
                            <th onClick={() => viewMode === 'pending' && handleSort('symbol')} style={{ cursor: viewMode === 'pending' ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>Symbol{viewMode === 'pending' ? ` ${getSortIndicator('symbol')}` : ''}</th>
                            <th onClick={() => viewMode === 'pending' && handleSort('original_account_number')} style={{ cursor: viewMode === 'pending' ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>Orig. Acct #{viewMode === 'pending' ? ` ${getSortIndicator('original_account_number')}` : ''}</th>
                            <th onClick={() => viewMode === 'pending' && handleSort('username')} style={{ cursor: viewMode === 'pending' ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>Requested By{viewMode === 'pending' ? ` ${getSortIndicator('username')}` : ''}</th>
                            <th onClick={() => viewMode === 'pending' && handleSort('requesteddate')} style={{ cursor: viewMode === 'pending' ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>
                              {viewMode === "processed" ? "Processed Date" : `Requested Date${viewMode === 'pending' ? ` ${getSortIndicator('requesteddate')}` : ''}`}
                            </th>
                            <th onClick={() => viewMode === 'pending' && handleSort('status')} style={{ cursor: viewMode === 'pending' ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>Status{viewMode === 'pending' ? ` ${getSortIndicator('status')}` : ''}</th>
                            <th onClick={() => viewMode === 'pending' && handleSort('method')} style={{ cursor: viewMode === 'pending' ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>Method{viewMode === 'pending' ? ` ${getSortIndicator('method')}` : ''}</th>
                            {viewMode === "pending" && <th>Action</th>}
                          </tr>
                        </thead>

                        <tbody>
                          {trades.map(trade => (
                            <tr
                                      key={trade.tradedetailid}
                                      className={
                                        trade.status === 'Completed'
                                          ? 'light-green-row'
                                          : trade.status === 'Cannot Submit'
                                          ? 'light-red-row'
                                          : selectedTradeIds.has(trade.tradedetailid)
                                          ? 'selected-row'
                                          : ''
                                      }
                                    >
                              {/* Per-row checkbox — only in pending view */}
                              {viewMode === "pending" && (
                                <td style={{ textAlign: "center" }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedTradeIds.has(trade.tradedetailid)}
                                    onChange={() => toggleTradeSelection(trade.tradedetailid)}
                                  />
                                </td>
                              )}
                              <td>{trade.buysell === 'B' ? 'Buy' : trade.buysell === 'S' ? 'Sell' : trade.buysell}</td>
                              <td>{trade.sharedollar === 'S' ? 'Shares' : trade.sharedollar === 'D' ? 'Dollar' : trade.sharedollar}</td>
                              <td>{trade.quantity}</td>
                              <td>{trade.percentage}</td>
                              <td>{trade.symbol}</td>
                               <td>{trade.original_account_number}</td>
                              <td>{trade.username}</td>
                                  <td>
                                    {viewMode === "processed"
                                      ? toLocalTime(trade.updateddatetime)
                                      : toLocalTime(trade.requesteddate)}
                                  </td>
                              <td>{trade.status}</td>
                              <td>{trade.method}</td>

                              {viewMode === "pending" && (
                                <td style={{ whiteSpace: "nowrap" }}>
                                  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                    {!selectedTradeIds.has(trade.tradedetailid) && (
                                      <button
                                        className="mark-complete-btn"
                                        onClick={() => markTradeAsComplete(trade.tradedetailid, trade.firm_slug)}
                                      >
                                        Mark Complete
                                      </button>
                                    )}
                                    {!selectedTradeIds.has(trade.tradedetailid) && (
                                      <button
                                        className="cannot-submit-btn"
                                        style={{ marginLeft: 0 }}
                                        onClick={() => openCannotSubmitModal(trade)}
                                      >
                                        Cannot Submit
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}

                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* ── Bulk action bar — appears when ≥1 row is checked ── */}
                      {viewMode === "pending" && accountSelectedCount > 0 && (
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "10px",
                          marginTop: "8px",
                          padding: "6px 4px"
                        }}>
                          <span style={{ fontSize: "13px", color: "#555" }}>
                            {accountSelectedCount} trade{accountSelectedCount > 1 ? 's' : ''} selected
                          </span>
                          <button
                            className="cannot-submit-btn"
                            style={{ backgroundColor: "#7a449a", borderColor: "#7a449a" }}
                            onClick={() => openMixedBulkModal(accountNumber, trades)}
                          >
                            Bulk Actions
                          </button>
                        </div>
                      )}
                      {/* ──────────────────────────────────────────────────── */}

                    </div>
                  )}

                </div>
                );
              })}

            </div>
          )}

        </div>
      ))}
            </div>
          )}
        </div>
      ))}

    </div>
 {/* ✅ PAGINATION CONTROLS */}
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>

              <span style={{ margin: "0 10px" }}>
                Page {page} of {totalPages}
              </span>

              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>

    {/* ===============================
        Single Trade — Cannot Submit Modal (existing)
    ================================ */}
    {showCannotSubmitModal && (
      <div className="modal-overlay">
        <div className="modal-box">

          <h3>Cannot Submit Trade</h3>

          <p>
            <strong>Account:</strong> {selectedTrade?.accountnumber}
          </p>

          <textarea
            rows="4"
            placeholder="Enter reason..."
            value={cannotSubmitReason}
            onChange={(e) => setCannotSubmitReason(e.target.value)}
          />

          <div className="modal-buttons">
            <button
              className="cancel-btn"
              onClick={() => setShowCannotSubmitModal(false)}
            >
              Cancel
            </button>

            <button
              className="submit-reject-btn"
              onClick={handleCannotSubmit}
            >
              Submit
            </button>
          </div>

        </div>
      </div>
    )}

    {/* ===============================
        Mixed Bulk Actions Modal
    ================================ */}
    {showMixedBulkModal && (
      <div className="modal-overlay">
        <div className="modal-box" style={{ maxWidth: "520px", width: "100%" }}>

          <h3>Bulk Actions</h3>

          <p style={{ marginBottom: "4px" }}>
            <strong>Account:</strong> {mixedBulkAccount}
          </p>

          <p style={{ fontSize: "13px", color: "#666", marginBottom: "12px" }}>
            Toggle each trade to <strong>Mark Complete</strong> or <strong>Cannot Submit</strong>.
          </p>

          {/* Per-trade action toggle */}
          <div style={{ maxHeight: "220px", overflowY: "auto", marginBottom: "14px", border: "1px solid #e5e7eb", borderRadius: "6px" }}>
            {mixedBulkTrades.map((t, idx) => {
              const action = mixedTradeActions[t.tradedetailid] || 'cannot_submit';
              const isComplete = action === 'complete';
              return (
                <div
                  key={t.tradedetailid}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    borderBottom: idx < mixedBulkTrades.length - 1 ? "1px solid #f0f0f0" : "none",
                    backgroundColor: isComplete ? "#f0fdf4" : "#fef2f2"
                  }}
                >
                  <span style={{ fontSize: "13px", color: "#333" }}>
                    {t.buysell === 'B' ? 'Buy' : 'Sell'} — <strong>{t.symbol}</strong>
                    {t.quantity ? ` · Qty: ${t.quantity}` : ''}
                  </span>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <button
                      style={{
                        padding: "3px 10px",
                        fontSize: "12px",
                        border: "1px solid #22c55e",
                        borderRadius: "4px",
                        backgroundColor: isComplete ? "#22c55e" : "white",
                        color: isComplete ? "white" : "#22c55e",
                        cursor: "pointer",
                        fontWeight: isComplete ? "700" : "normal"
                      }}
                      onClick={() => isComplete ? null : toggleMixedTradeAction(t.tradedetailid)}
                    >
                      ✓ Complete
                    </button>
                    <button
                      style={{
                        padding: "3px 10px",
                        fontSize: "12px",
                        border: "1px solid #ef4444",
                        borderRadius: "4px",
                        backgroundColor: !isComplete ? "#ef4444" : "white",
                        color: !isComplete ? "white" : "#ef4444",
                        cursor: "pointer",
                        fontWeight: !isComplete ? "700" : "normal"
                      }}
                      onClick={() => !isComplete ? null : toggleMixedTradeAction(t.tradedetailid)}
                    >
                      ✗ Cannot Submit
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary counts */}
          <p style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
            <span style={{ color: "#16a34a", fontWeight: "600" }}>
              {mixedBulkTrades.filter(t => mixedTradeActions[t.tradedetailid] === 'complete').length} Complete
            </span>
            {" · "}
            <span style={{ color: "#dc2626", fontWeight: "600" }}>
              {mixedBulkTrades.filter(t => mixedTradeActions[t.tradedetailid] !== 'complete').length} Cannot Submit
            </span>
          </p>

          {/* Reason — only shown if any are cannot_submit */}
          {mixedBulkTrades.some(t => mixedTradeActions[t.tradedetailid] !== 'complete') && (
            <textarea
              rows="3"
              placeholder="Enter reason for cannot-submit trades..."
              value={mixedBulkReason}
              onChange={(e) => setMixedBulkReason(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box" }}
            />
          )}

          <div className="modal-buttons">
            <button
              className="cancel-btn"
              onClick={() => {
                setShowMixedBulkModal(false);
                setMixedBulkReason('');
              }}
            >
              Cancel
            </button>
            <button
              className="submit-reject-btn"
              onClick={handleMixedBulkAction}
            >
              Submit
            </button>
          </div>

        </div>
      </div>
    )}

  </div>
);
};

export default TraderDashboard;
